import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import AuthService from '../../services/AuthService';
import Loader from '../../components/Loader';
import Paginator from '../../components/Paginator';
import moment from 'moment';

const PartnerNotifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({ page: 1, limit: 20 });

  useEffect(() => {
    if (!AuthService.getCurrentpartner()) { navigate('/partner/login'); return; }
    getNotifications();
  }, [filters]);

  const getNotifications = async () => {
    try {
      setLoading(true);
      const res = await ApiService.request({
        method: 'GET',
        url: 'partner/getNotifications',
        params: { page: filters.page, limit: filters.limit },
      });
      if (res.data.status) {
        setNotifications(res.data.data?.notifications || []);
        setPagination(res.data.data?.pagination || {});
      }
    } catch {
      toast.error('Error fetching notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await ApiService.request({ method: 'POST', url: 'partner/markReadNotifications', data: { notification_id: 'all' } });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark notifications as read');
    }
  };

  const clearAll = async () => {
    try {
      setLoading(true);
      const res = await ApiService.request({ method: 'POST', url: 'partner/clearAllNotifications' });
      if (res.data.status) {
        setNotifications([]);
        toast.success('All notifications cleared successfully');
        getNotifications();
      } else {
        toast.error(res.data.message || 'Failed to clear notifications');
      }
    } catch {
      toast.error('Failed to clear notifications');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => setFilters(prev => ({ ...prev, page }));

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Toolbar */}
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              Notifications
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <a href="/partner/dashboard" className="text-muted text-hover-primary">Dashboard</a>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">Notifications</li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 gap-lg-3">
            {notifications.length > 0 && (
              <>
                <button className="btn btn-sm btn-light-primary" onClick={markAllRead} disabled={loading}>
                  <i className="bi bi-check2-all me-2"></i>Mark All Read
                </button>
                <button
                  className="btn btn-sm text-white btn-base"
                  onClick={clearAll}
                  disabled={loading}
                >
                  <i className="bi bi-trash me-2"></i>Clear All
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <h3>All Notifications</h3>
              </div>
            </div>
            <div className="card-body pt-0">
              {loading ? (
                <Loader />
              ) : notifications.length > 0 ? (
                <>
                  <div className="list-group list-group-flush">
                    {notifications.map((notification, index) => (
                      <div
                        key={notification._id || index}
                        className="list-group-item border-bottom"
                        style={{ background: notification.is_read ? '#fff' : '#fdfbf5ff' }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <span className="text-dark fw-semibold fs-6 me-2">{notification.title}</span>
                              {!notification.is_read && (
                                <span className="badge badge-light-danger">New</span>
                              )}
                            </div>
                            <p className="text-gray-700 mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                              {notification.message}
                            </p>
                          </div>
                          <div className="ms-3 text-end">
                            <small className="text-muted d-block">
                              {moment(notification.createdAt).format('MMM DD, YYYY')}
                            </small>
                            <small className="text-muted">
                              {moment(notification.createdAt).fromNow()}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-5">
                      <Paginator
                        currentPage={pagination.current_page || filters.page}
                        totalPages={pagination.total_pages || 1}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-10">
                  <i className="bi bi-bell-slash fs-3x text-gray-400 mb-5"></i>
                  <h3 className="text-gray-600 mb-2">No Notifications</h3>
                  <p className="text-gray-500">You don't have any notifications at the moment.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerNotifications;
